import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useCallback, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import useNRQuery from '../../../../graphql/useNRQuery';
import { GET_VULNERABILITY_JIRA_TICKETS } from '../../../../graphql/queries/jiraIntegration';
import JiraIcon from '../../../../NRIcons/IntegrationIcons/JiraIcon';
import { FiltersContext } from '../../../../pages/NRReportTabs/NRReportFilterContextWrapper';
import useIsJiraIntegrationActive from '../../../../pages/NRSettings/NRIntegrations/NRJiraIntegration/useIsJiraIntegrationActive';
import { CREATE_JIRA_TICKET_LABELS } from './createJiraTicket.constants';
import NRCreateVulnerabilityJiraTicket from './NRCreateVulnerabilityJiraTicket';
import NRJiraTicketsTable from './NRJiraTicketsTable';
import { getTicketCountLabel, VULNERABILITY_JIRA_TICKETS_LABELS } from './vulnerabilityJiraTickets.constants';

function NRVulnerabilityJiraTickets({ advisoryId, vulnerabilityData, cveDetails, sx }) {
  const theme = useTheme();
  const { componentId: assetId } = useParams();
  const { isJiraIntegrationActive } = useIsJiraIntegrationActive();
  const { drawerContent, setDrawerContent, drawerTitle, setDrawerTitle, setHasBack, setOpenDrawer } = useContext(FiltersContext);

  const componentId = vulnerabilityData?.componentId || null;

  const ticketArgs = useMemo(
    () => ({
      assetId,
      advisoryId,
      componentId
    }),
    [advisoryId, assetId, componentId]
  );

  const [tickets, ticketsLoading] = useNRQuery({
    query: GET_VULNERABILITY_JIRA_TICKETS,
    options: {
      variables: { args: ticketArgs },
      skip: !isJiraIntegrationActive || !assetId || !advisoryId || !componentId
    },
    qlObjKey: 'vulnerabilityJiraTickets',
    defaultValue: []
  });

  const handleCreateTicket = useCallback(
    event => {
      event?.stopPropagation();

      if (drawerContent.length) {
        setHasBack(true);
      }

      setDrawerTitle([...drawerTitle, CREATE_JIRA_TICKET_LABELS.DRAWER_TITLE]);
      setDrawerContent([
        ...drawerContent,
        <NRCreateVulnerabilityJiraTicket
          key="create-jira-ticket"
          vulnerabilityData={vulnerabilityData}
          cveDetails={cveDetails}
          assetId={assetId}
          advisoryId={advisoryId}
        />
      ]);
      setOpenDrawer(true);
    },
    [
      advisoryId,
      assetId,
      cveDetails,
      drawerContent,
      drawerTitle,
      setDrawerContent,
      setDrawerTitle,
      setHasBack,
      setOpenDrawer,
      vulnerabilityData
    ]
  );

  if (!isJiraIntegrationActive) {
    return null;
  }

  const sectionSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    mt: theme.spacing(2)
  };

  const ticketCountLabel = tickets.length ? getTicketCountLabel(tickets.length) : VULNERABILITY_JIRA_TICKETS_LABELS.NO_TICKETS;

  const sectionHeaderContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        minWidth: 0
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Avatar
          sx={{
            width: theme.spacing(6),
            height: theme.spacing(6),
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.iconInfo
          }}
        >
          <JiraIcon width={theme.spacing(4)} height={theme.spacing(4)} />
        </Avatar>
        <Box sx={{ ml: theme.spacing(1), minWidth: 0 }}>
          <Typography variant="label" color="text.secondary">
            {VULNERABILITY_JIRA_TICKETS_LABELS.SECTION_LABEL}
          </Typography>
          <Typography variant="h3" color="text.primary">
            {ticketCountLabel}
          </Typography>
        </Box>
      </Box>
      <Button variant="outlined" color="secondary" onClick={handleCreateTicket} sx={{ flexShrink: 0, ml: theme.spacing(2) }}>
        {VULNERABILITY_JIRA_TICKETS_LABELS.CREATE_TICKET}
      </Button>
    </Box>
  );

  const sectionHeader = <Box sx={{ p: theme.spacing(2) }}>{sectionHeaderContent}</Box>;

  return (
    <Box sx={[sectionSx, sx]}>
      {ticketsLoading ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: theme.spacing(3)
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : tickets.length ? (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            '&.Mui-expanded': { margin: 0 }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              px: theme.spacing(2),
              py: theme.spacing(2),
              '& .MuiAccordionSummary-content': {
                margin: 0,
                width: '100%'
              }
            }}
          >
            {sectionHeaderContent}
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <NRJiraTicketsTable tickets={tickets} />
          </AccordionDetails>
        </Accordion>
      ) : (
        sectionHeader
      )}
    </Box>
  );
}

export default NRVulnerabilityJiraTickets;
